import { Injectable } from '@angular/core';

declare global {
    interface Window {
        clarity: any;
    }
}

@Injectable({
    providedIn: 'root'
})
export class ClarityService {

    constructor() { }

    /**
     * Track custom events in Clarity
     * @param eventName - Name of the custom event
     * @param eventData - Additional data for the event
     */
    trackEvent(eventName: string, eventData?: any): void {
        if (window.clarity) {
            window.clarity('event', eventName, eventData);
        }
    }

    /**
     * Set user ID for Clarity tracking
     * @param userId - User identifier
     */
    setUserId(userId: string): void {
        if (window.clarity) {
            window.clarity('identify', userId);
        }
    }

    /**
     * Track page views
     * @param pageName - Name of the page
     */
    trackPageView(pageName: string): void {
        if (window.clarity) {
            window.clarity('event', 'page_view', { page: pageName });
        }
    }

    /**
     * Track user actions
     * @param action - Action performed
     * @param details - Additional details
     */
    trackUserAction(action: string, details?: any): void {
        if (window.clarity) {
            window.clarity('event', 'user_action', { action, ...details });
        }
    }

    /**
     * Track form submissions
     * @param formName - Name of the form
     * @param success - Whether submission was successful
     */
    trackFormSubmission(formName: string, success: boolean): void {
        if (window.clarity) {
            window.clarity('event', 'form_submission', {
                form: formName,
                success,
                timestamp: new Date().toISOString()
            });
        }
    }

    /**
     * Track assessment creation
     * @param assessmentName - Name of the assessment
     * @param questionTypes - Types of questions included
     */
    trackAssessmentCreation(assessmentName: string, questionTypes: string[]): void {
        if (window.clarity) {
            window.clarity('event', 'assessment_created', {
                assessment_name: assessmentName,
                question_types: questionTypes,
                timestamp: new Date().toISOString()
            });
        }
    }

    /**
     * Track candidate invitation
     * @param assessmentName - Name of the assessment
     * @param candidateCount - Number of candidates invited
     */
    trackCandidateInvitation(assessmentName: string, candidateCount: number): void {
        if (window.clarity) {
            window.clarity('event', 'candidates_invited', {
                assessment_name: assessmentName,
                candidate_count: candidateCount,
                timestamp: new Date().toISOString()
            });
        }
    }

    /**
     * Track login events
     * @param userType - Type of user (admin, candidate, etc.)
     * @param success - Whether login was successful
     */
    trackLogin(userType: string, success: boolean): void {
        if (window.clarity) {
            window.clarity('event', 'login_attempt', {
                user_type: userType,
                success,
                timestamp: new Date().toISOString()
            });
        }
    }

    /**
     * Check if Clarity is loaded
     * @returns True if Clarity is available
     */
    isClarityLoaded(): boolean {
        return typeof window.clarity !== 'undefined';
    }
} 